import { AuthStatus, type Auth } from '@/auth/auth'
import {
    createLogPrefix,
    isSilentError,
    logDebug,
    type LogTarget,
    submitErrorReport,
} from '@amzn/vinyl-util'
import type { AuthTokenProvider, AuthTokenWithRefresh } from '@amzn/vinyl-amzn'
import {
    exchangeCodeForToken,
    oAuthAuthorize,
    type OAuthOptions,
    refreshToken,
} from '@/auth/oAuth2'
import { localSignInPersistence } from '@/auth/signInPersistence'
import { decrypt } from '@/util/crypto'
import { data, type ObservableValue } from '@amzn/vinyl-observable'

const OAUTH_CONFIG = {
    // Using DragonFly Web's security profile
    clientId: 'amzn1.application-oa2-client.339f46ec317249bd8507340b1ec9c7d7',
    authorizationEndpoint: 'https://www.amazon.com/ap/oa',
    tokenEndpoint: 'https://api.amazon.com/auth/o2/token',
    scopes: ['profile'],
    clientSecret: decrypt(
        // Temporary: will switch to AuthPortal
        'ZTpkfbm5UZ4oXORtsCoq2Ywm+5Il1I9o9U9PaePB6XLvO8ydcGiKlZXCPFXM/PUHTLjpd8KdVORLyiobP1rf/Y1zBA9Y9rXzziztgDHz4JD7O8+W06Mt++UwGGn+AcDCbN7AdSgTgpMASmIz',
        'bullfrogInsecurePw'
    ),
    redirectUri: new URL('login.html', location.origin).href,
} as const satisfies OAuthOptions

export class LwaAuth implements Auth, LogTarget {
    logPrefix = createLogPrefix('LwaAuth')

    private _authTokenProvider: AuthTokenProvider | null = null

    private _status = data(AuthStatus.PENDING)

    constructor() {
        localSignInPersistence.get().then(this.setToken).catch(this.errorHandler)
    }

    get status(): ObservableValue<AuthStatus> {
        return this._status
    }

    isSignedIn(): boolean {
        return this._authTokenProvider != null
    }

    get authTokenProvider(): AuthTokenProvider {
        if (this._authTokenProvider == null) throw new Error('not signed in')
        return this._authTokenProvider
    }

    signIn(): void {
        oAuthAuthorize(OAUTH_CONFIG)
            .then((code) => {
                logDebug(this, `code retrieved: ${code}`)
                exchangeCodeForToken(OAUTH_CONFIG, code)
                    .then(this.setToken)
                    .catch(this.errorHandler)
            })
            .catch(this.errorHandler)
    }

    signOut(): void {
        // TODO: revoke token
        this.setToken(null)
    }

    private errorHandler = (error: any) => {
        if (isSilentError(error)) return
        submitErrorReport({
            error,
            target: this,
        })
        this.signOut()
    }

    private setToken = (token: AuthTokenWithRefresh | null) => {
        if (token) {
            this._authTokenProvider = createAuthTokenProvider(token, (newToken) => {
                // on refresh
                localSignInPersistence.set(newToken).catch(this.errorHandler)
            })
            localSignInPersistence.set(token).catch(this.errorHandler)
            this._status.value = AuthStatus.SIGNED_IN
        } else {
            localSignInPersistence.remove().catch(this.errorHandler)
            if (this._authTokenProvider) {
                this._authTokenProvider = null
            }
            this._status.value = AuthStatus.SIGNED_OUT
        }
    }
}

function createAuthTokenProvider(
    token: AuthTokenWithRefresh,
    onRefreshed?: (token: AuthTokenWithRefresh) => void
): AuthTokenProvider {
    let authTokenRefreshPromise: Promise<AuthTokenWithRefresh> | null = null
    return async () => {
        if (token.expiresAt > Date.now() + 60 * 5 * 1000) {
            // Does not expire in the next 5 minutes, return the current token.
            return token.accessToken
        } else {
            if (!authTokenRefreshPromise) {
                authTokenRefreshPromise = refreshToken(OAUTH_CONFIG, token.refreshToken).finally(
                    () => (authTokenRefreshPromise = null)
                )
            }
            token = await authTokenRefreshPromise
            onRefreshed?.(token)
            return token.accessToken
        }
    }
}
